import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający dostępem do modułu Klientów B2B (Clients).
 * Weryfikuje uprawnienia użytkownika (Role-Based Access Control) do przeglądania
 * oraz edycji danych klientów.
 * Realizuje walidację po stronie serwera (Server-Side Validation).
 */
@Injectable({
  providedIn: 'root'
})
export class ClientsAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne załadowanie (Lazy Loading) modułu Klientów.
   * Chroni kod źródłowy modułu przed pobraniem przez nieuprawnioną przeglądarkę,
   * wykonując strzał weryfikacyjny do API przed rozpoczęciem transferu plików JS.
   *
   * @param route Konfiguracja ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący true, jeśli użytkownik posiada odpowiednie role systemowe.
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/clients-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      })
      , map(response => {
        console.log('check - clients - ok');
        return true;
      }));
  }

  /**
   * Weryfikuje dostęp przed aktywacją konkretnego widoku wewnątrz modułu Klientów.
   * W przypadku braku uprawnień (innych niż błąd sesji 401), deleguje wyświetlenie
   * komunikatu błędu (Toast) do klasy narzędziowej CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący decyzję o dopuszczeniu do nawigacji.
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/clients-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - clients - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
